import { FunctionsHttpError } from '@supabase/supabase-js';
import { getSupabase } from './client';
import type { MemberRole } from './database.types';
import { DataError, toDataError, type DataErrorCode } from './errors';
import { asJsonObject, asString } from './json';
import { grantsToJson, type Grants } from './workspace';

/**
 * RBAC account management. Creating users needs the service-role key, which must never
 * reach a browser — so these calls go to the `admin-users` Edge Function, which verifies
 * the caller's JWT and that the caller is an active admin of the workspace.
 */

export interface CreateMemberInput {
  readonly workspaceId: string;
  readonly appKey: string;
  readonly email: string;
  readonly password: string;
  readonly displayName: string;
  readonly role: MemberRole;
  readonly empCode: string | null;
  readonly grants: Grants;
}

export interface CreateMemberResult {
  readonly userId: string;
  /** true when the email already had an Auth account (e.g. member of another workspace). */
  readonly existingUser: boolean;
}

const FUNCTION_NAME = 'admin-users';

function codeFromStatus(status: number): DataErrorCode {
  if (status === 400 || status === 422) return 'invalid';
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  return 'unknown';
}

async function invoke(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const { data, error } = await getSupabase().functions.invoke<Record<string, unknown>>(FUNCTION_NAME, {
    body,
  });

  if (error) {
    if (error instanceof FunctionsHttpError) {
      const response: unknown = error.context;
      if (response instanceof Response) {
        let message = `${response.status} ${response.statusText}`;
        try {
          const payload = asJsonObject(await response.json());
          message = asString(payload.message, message);
        } catch {
          // non-JSON error body: keep the status line
        }
        throw new DataError(codeFromStatus(response.status), message, error);
      }
    }
    throw toDataError(error);
  }
  return data ?? {};
}

export async function createMember(input: CreateMemberInput): Promise<CreateMemberResult> {
  const result = await invoke({
    action: 'create',
    workspaceId: input.workspaceId,
    appKey: input.appKey,
    email: input.email.trim().toLowerCase(),
    password: input.password,
    displayName: input.displayName,
    role: input.role,
    empCode: input.empCode,
    grants: grantsToJson(input.grants),
  });
  return { userId: asString(result.userId), existingUser: result.existingUser === true };
}

export interface SaveMemberInput {
  readonly userId: string;
  readonly displayName?: string;
  readonly role?: MemberRole;
  readonly empCode?: string | null;
  readonly grants?: Grants;
  readonly menus?: Record<string, boolean> | null;
}

/**
 * Persists role / name / employee-link / grants edits. This goes through the Edge Function
 * rather than writing the tables directly: the function re-checks that the caller is an
 * active admin and then writes with the service-role key, so the save cannot be rejected by
 * an RLS policy that has drifted on the project.
 */
export async function saveMembers(
  workspaceId: string,
  appKey: string,
  members: readonly SaveMemberInput[],
): Promise<void> {
  if (members.length === 0) return;
  await invoke({
    action: 'save_members',
    workspaceId,
    appKey,
    members: members.map((m) => {
      const entry: Record<string, unknown> = { userId: m.userId };
      if (m.displayName !== undefined) entry.displayName = m.displayName;
      if (m.role !== undefined) entry.role = m.role;
      if (m.empCode !== undefined) entry.empCode = m.empCode;
      if (m.grants !== undefined) entry.grants = grantsToJson(m.grants);
      if (m.menus !== undefined) entry.menus = m.menus;
      return entry;
    }),
  });
}

/**
 * Changes a member's role. The server applies that role's permission matrix
 * (workspace_settings.ROLE_GRANTS) in the same transaction, so the caller never computes
 * grants: admin receives '*', manager and employee receive their stored template.
 */
export async function setMemberRole(
  workspaceId: string,
  appKey: string,
  userId: string,
  role: MemberRole,
): Promise<void> {
  await invoke({ action: 'set_role', workspaceId, appKey, userId, role });
}

export async function setMemberPassword(workspaceId: string, userId: string, password: string): Promise<void> {
  await invoke({ action: 'set_password', workspaceId, userId, password });
}

export async function setMemberActive(workspaceId: string, userId: string, active: boolean): Promise<void> {
  await invoke({ action: 'set_active', workspaceId, userId, active });
}

export async function deleteMember(workspaceId: string, userId: string): Promise<void> {
  await invoke({ action: 'delete', workspaceId, userId });
}
